import React from 'react'
import Auth2 from 'plugin/src/shared/Auth2'
import { getTZOffset } from 'plugin/src/shared/Utils'
import PropTypes from 'prop-types'
import { apiClient } from '@mote/api/src/client'
import { getRationing, shouldDebitMote } from '@mote/helpers/identity'

export const FlightRecorderContext = React.createContext({})

const propTypes = {
  children: PropTypes.oneOfType([PropTypes.array, PropTypes.object]).isRequired
}

const DEFAULT_STATE = {
  requests: {},
  moteId: null,
  chunksErrorCount: 0,
  chunksErrorThreshold: 50,
  shouldDebit: shouldDebitMote(),
  ...getRationing()
}

export const FlightRecorderProvider = ({ children }) => {
  const [state, setState] = React.useState(DEFAULT_STATE)

  const registerRequest = React.useCallback(route => {
    setState(prev => ({
      ...prev,
      requests: {
        ...prev.requests,
        [route]: 1
      }
    }))
  }, [])

  const bumpRequestRetries = React.useCallback(route => {
    setState(prev => ({
      ...prev,
      requests: {
        ...prev.requests,
        [route]: prev.requests[route] + 1
      }
    }))
  }, [])

  const deregisterRequest = React.useCallback(route => {
    setState(prev => {
      const requests = { ...prev.requests }

      delete requests[route]
      return {
        ...prev,
        requests
      }
    })
  }, [])

  const increaseChunksErrorCount = React.useCallback(route => {
    setState(prev => {
      const requests = { ...prev.requests }

      delete requests[route]
      return {
        ...prev,
        requests,
        chunksErrorCount: prev.chunksErrorCount + 1
      }
    })
  }, [])

  const assignMoteId = React.useCallback(moteId => {
    setState(prev => {
      const requests = { ...prev.requests }

      delete requests['m/register']
      return {
        ...prev,
        requests,
        moteId
      }
    })
  }, [])

  const handleFinalizeSuccess = React.useCallback(() => {
    setState(prev => ({ ...prev, finalized: true }))
  }, [])

  const resetState = React.useCallback(() => {
    checkRationing(DEFAULT_STATE)
  }, [])

  const checkStatusAndUpdateRationing = React.useCallback(
    moteId => {
      setTimeout(async () => {
        apiClient
          .extend({
            timeout: 15000,
            retry: {
              limit: 3,
              methods: ['post']
            }
          })
          .post(`v1/m/status/${moteId || state.moteId}`, {
            json: {
              TZOffset: getTZOffset()
            }
          })
          .json()
          .then(_ => {
            checkRationing()
          })
          .catch(err => console.error('error', err.message))
      }, 5000)
    },
    [state.moteId]
  )

  const checkRationing = React.useCallback((stateOverride = {}) => {
    setState(prev => ({
      ...prev,
      ...stateOverride,
      shouldDebit: shouldDebitMote(),
      ...getRationing()
    }))
  }, [])

  React.useEffect(() => {
    checkRationing()
    Auth2.addListener('flightRecorderRationing', checkRationing)
    return Auth2.removeListener('flightRecorderRationing')
  }, [])

  return (
    <FlightRecorderContext.Provider
      value={{
        flightState: state,
        registerRequest,
        bumpRequestRetries,
        deregisterRequest,
        assignMoteId,
        handleFinalizeSuccess,
        increaseChunksErrorCount,
        checkRationing,
        checkStatusAndUpdateRationing,
        resetFlightState: resetState
      }}
    >
      {children}
    </FlightRecorderContext.Provider>
  )
}

FlightRecorderProvider.propTypes = propTypes
